/**
 * LocationMessage Component
 *
 * Renders location chat messages with map preview and address.
 * Supports reply context and links to Google Maps.
 */

import React, { FC } from "react"
import clsx from "clsx"
import ChatMessageReplyView from "src/app/layout/chat-handle/components/chat-replymessage/ChatMessageReplyView"
import { MessageLocation } from "src/app/layout/chat-handle/components/chat-message/ChatLocation"
import { LocationMessageProps } from "../../types/MessageTypes"

const LocationMessage: FC<LocationMessageProps> = ({
  message,
  bgChat,
  txChat,
  isTabletOrMobile,
  replyMessageID,
  customer,
  phoneNumber,
}) => {
  const widthClass = isTabletOrMobile ? "w-200px" : "w-300px"
  const maxWidthClass = isTabletOrMobile ? "mw-200px" : "mw-300px"

  const mapsUrl = `https://maps.google.com/maps?q=${message.location?.latitude},${message.location?.longitude}&z=17`

  /**
   * Renders the location caption (name + address)
   */
  const renderCaption = () => (
    <div
      className={clsx("rounded", bgChat, "fluid align-items-center")}
      style={{ color: txChat, paddingTop: "5px" }}
    >
      {message.location?.locationName && (
        <div data-testid="inboundchat-locationname">
          <span className="bi bi-geo-alt-fill" style={{ paddingRight: "5px" }} />
          {message.location.locationName}
        </div>
      )}
      {message.location?.locationAddress && <div>{message.location.locationAddress}</div>}
    </div>
  )

  /**
   * Renders the location content
   */
  const renderLocation = () => (
    <div
      className={clsx("p-5 rounded fw-bold", bgChat, widthClass)}
      style={{ color: txChat }}
    >
      {/* Map preview */}
      <a href={mapsUrl} data-testid="inboundchat-location" target="_blank" rel="noopener noreferrer">
        <div className={maxWidthClass}>
          <MessageLocation item={message} />
        </div>
      </a>

      {/* Caption */}
      {renderCaption()}
    </div>
  )

  /**
   * Renders location content for reply view (slightly different layout)
   */
  const renderLocationForReply = () => (
    <div style={{ color: txChat }} className="fw-bold">
      <a href={mapsUrl} data-testid="inboundchat-location" target="_blank" rel="noopener noreferrer">
        <div className={maxWidthClass}>
          <MessageLocation item={message} />
        </div>
      </a>
      {renderCaption()}
    </div>
  )

  /**
   * Renders with reply context
   */
  const renderWithReply = () => (
    <ChatMessageReplyView
      bgChat={bgChat}
      replyMessageID={replyMessageID}
      customer={customer}
      phoneNumber={phoneNumber}
    >
      {renderLocationForReply()}
    </ChatMessageReplyView>
  )

  // Render with or without reply context
  if (message.replyMessageID) {
    return renderWithReply()
  }

  return renderLocation()
}

export default LocationMessage
